import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  adminListCategories,
  adminCreateCategory,
  adminUpdateCategory,
  adminDeleteCategory,
  adminUploadCategoryImage,
} from "../../services/api";
import { getProductImage } from "../../data/images";
import { COLLECTION_ART } from "../../components/shop/CollectionBanner";
import { COLLECTION_COPY } from "../../data/products";

const label = "text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal";
const input = "mt-2 w-full border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-orange";
const emptyForm = { name: "", slug: "", image: "", active: true };

export default function AdminCategories() {
  const { user, ready } = useAuth();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slug or "new" or null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminListCategories().then(setCats).catch(() => toast.error("Could not load categories.")).finally(() => setLoading(false));
  };
  useEffect(() => { if (ready && user?.role === "admin") load(); }, [ready, user]);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (c) => { setForm({ name: c.name, slug: c.slug, image: c.image || "", active: c.active !== false }); setEditing(c.slug); };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const uploadImage = async (file) => {
    if (!file) return;
    try { const res = await adminUploadCategoryImage(file); setForm((f) => ({ ...f, image: res.path })); toast.success("Image uploaded."); }
    catch { toast.error("Image upload failed."); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    setSaving(true);
    try {
      if (editing === "new") { await adminCreateCategory({ name: form.name, slug: form.slug || undefined, image: form.image, active: form.active }); toast.success("Category created."); }
      else { await adminUpdateCategory(editing, { name: form.name, image: form.image, active: form.active }); toast.success("Category updated."); }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await adminDeleteCategory(c.slug); toast.success("Category deleted."); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <AdminLayout title="Categories" subtitle={`${cats.length} categories`}
      actions={<button onClick={openNew} data-testid="category-add-btn" className="flex items-center gap-2 bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-orange"><Plus className="h-4 w-4" /> Add</button>}>
      <div data-testid="admin-categories-page">
        {loading ? <p className="text-brand-stone" data-testid="categories-loading">Loading…</p> : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cats.map((c) => (
              <div key={c.slug} className="border border-brand-sand bg-brand-surface p-4" data-testid={`category-${c.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden bg-brand-cream">
                    {c.image ? <img src={getProductImage(c.image, c.name).src} alt={c.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center font-serif text-brand-orange/50">H</div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-brand-charcoal">{c.name}</p>
                    <p className="truncate text-xs text-brand-stone">{c.slug} · {c.product_count ?? 0} products</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${c.active !== false ? "bg-brand-orange/10 text-brand-orange" : "bg-brand-sand/50 text-brand-stone"}`}>{c.active !== false ? "Active" : "Inactive"}</span>
                  <div className="flex gap-3">
                    <button onClick={() => openEdit(c)} data-testid={`category-edit-${c.slug}`} className="text-brand-stone hover:text-brand-orange"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(c)} data-testid={`category-delete-${c.slug}`} className="text-brand-stone hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-charcoal/50 p-6" data-testid="category-modal">
          <div className="w-full max-w-md bg-brand-cream p-6">
            <p className="font-serif text-2xl text-brand-charcoal">{editing === "new" ? "New category" : "Edit category"}</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className={label} htmlFor="c-name">Name</label>
                <input id="c-name" data-testid="category-field-name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              {editing === "new" && (
                <div>
                  <label className={label} htmlFor="c-slug">Slug (optional)</label>
                  <input id="c-slug" data-testid="category-field-slug" className={input} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto from name" />
                </div>
              )}
              <div>
                <label className={label}>Campaign banner image</label>
                <p className="mt-1 text-xs text-brand-stone">Shown as this collection's hero banner on its shop page. Landscape photos (e.g. 1920×1080) work best.</p>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-16 w-24 overflow-hidden bg-white">
                    {form.image ? <img src={getProductImage(form.image, form.name).src} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-brand-stone">Default</div>}
                  </div>
                  <label data-testid="category-image-upload" className="cursor-pointer border border-brand-sand px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal hover:border-brand-orange">
                    Upload<input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} />
                  </label>
                  {form.image && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, image: "" }))} data-testid="category-image-remove" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-stone hover:text-red-600">
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {(form.image || COLLECTION_ART[form.slug]) && (
                <div data-testid="category-banner-preview">
                  <label className={label}>Banner preview</label>
                  <div className="relative mt-2 aspect-[16/7] overflow-hidden border border-brand-sand bg-brand-cream">
                    <img
                      src={form.image ? getProductImage(form.image, form.name).src : COLLECTION_ART[form.slug].img}
                      alt=""
                      className={`absolute inset-y-0 right-0 h-full w-1/2 object-cover ${form.image ? "object-[50%_25%]" : COLLECTION_ART[form.slug].pos}`}
                      data-testid="category-banner-preview-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-cream via-brand-cream/45 via-35% to-transparent" />
                    <div className="absolute left-4 top-1/2 max-w-[45%] -translate-y-1/2">
                      <p className="text-[8px] font-semibold uppercase tracking-[0.28em] text-brand-gold">The Collection</p>
                      <p className="mt-1 font-serif text-lg leading-tight text-brand-charcoal">{form.name || "Collection"}</p>
                      <p className="mt-1 truncate font-serif text-xs italic text-brand-stone">{COLLECTION_COPY[form.slug]?.tagline || "A new HESHAURA collection."}</p>
                    </div>
                  </div>
                  <p className="mt-1.5 text-[11px] text-brand-stone">
                    {form.image ? "Your uploaded campaign photo, as it will appear on the collection page." : "No custom photo yet — the default Heena campaign art will be used."}
                  </p>
                </div>
              )}
              <label className="flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
                <input type="checkbox" data-testid="category-field-active" className="h-4 w-4 accent-brand-orange" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} data-testid="category-save" className="flex-1 bg-brand-charcoal py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream hover:bg-brand-orange disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
              <button onClick={close} data-testid="category-cancel" className="flex-1 border border-brand-sand py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal hover:border-brand-orange">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
